/**
 * Route constants and utilities for the application
 * Centralizes public route definitions for consistent access control
 */

// Public routes that don't require authentication or budget file initialization
export const PUBLIC_ROUTES = new Set([
  '/',              // Landing page
  '/signin',        // Sign in page
  '/privacy-policy', // Privacy policy page
  '/tos',           // Terms of service page
  '/blog',          // Blog section (all blog routes)
  '/buddy-invite',  // Invite landing page — must be reachable while logged out
]);

/**
 * Checks if a given pathname is a public route
 * @param pathname - The pathname to check (from usePathname hook)
 * @returns true if the route is public, false otherwise
 */
export const isPublicRoute = (pathname: string | null): boolean => {
  if (!pathname) return false;
  
  // Check exact match for root
  if (pathname === '/') return PUBLIC_ROUTES.has('/');
  
  // Check if pathname starts with any public route
  // This handles nested routes like /blog/article-id
  return Array.from(PUBLIC_ROUTES).some(route => 
    route !== '/' && pathname.startsWith(route)
  );
};

/**
 * Protected routes that require authentication
 * Used for middleware and navigation guards
 */
export const PROTECTED_ROUTES = [
  '/dashboard',
  '/budget',
  '/transactions',
  '/accounts',
  '/settings',
  '/goals',
  '/buddies'
];

/**
 * Checks if a given pathname is a protected route
 * @param pathname - The pathname to check
 * @returns true if the route requires authentication, false otherwise
 */
export const isProtectedRoute = (pathname: string | null): boolean => {
  if (!pathname) return false;
  
  return PROTECTED_ROUTES.some(route => pathname.startsWith(route));
};